'use client';

import { useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface SearchResult {
  type: 'schedule' | 'room' | 'staff';
  id: string;
  title: string;
  subtitle: string;
  badge?: string;
  href?: string;
}

export function useQuickSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const supabase = createClient();

  const search = useCallback(
    async (q: string) => {
      setQuery(q);
      if (!q.trim() || q.length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      const term = q.toLowerCase();

      const [scheduleRes, roomsRes, staffRes] = await Promise.all([
        supabase
          .from('schedule_items')
          .select('id, title, date, time_start, branch, owner_name, location')
          .or(`title.ilike.%${term}%,owner_name.ilike.%${term}%,location.ilike.%${term}%`)
          .limit(5),
        supabase
          .from('rooms')
          .select('id, name, room_type, related_branch')
          .or(`name.ilike.%${term}%,related_object.ilike.%${term}%`)
          .limit(5),
        supabase
          .from('user_profiles')
          .select('id, full_name, role, email')
          .or(`full_name.ilike.%${term}%,email.ilike.%${term}%`)
          .limit(5),
      ]);

      const scheduleResults: SearchResult[] = (scheduleRes.data ?? []).map((item) => ({
        type: 'schedule' as const,
        id: item.id,
        title: item.title,
        subtitle: `${item.date} · ${item.time_start}${item.location ? ` · ${item.location}` : ''}`,
        badge: item.branch,
        href: '/schedule',
      }));

      const roomResults: SearchResult[] = (roomsRes.data ?? []).map((room) => ({
        type: 'room' as const,
        id: room.id,
        title: room.name,
        subtitle: room.room_type,
        badge: room.related_branch ?? undefined,
        href: '/rooms',
      }));

      const staffResults: SearchResult[] = (staffRes.data ?? []).map((staff) => ({
        type: 'staff' as const,
        id: staff.id,
        title: staff.full_name,
        subtitle: staff.email,
        badge: staff.role,
        href: '/team',
      }));

      setResults([...scheduleResults, ...roomResults, ...staffResults]);
      setLoading(false);
    },
    []
  );

  const clear = useCallback(() => {
    setQuery('');
    setResults([]);
  }, []);

  return { query, results, loading, search, clear };
}
